/**
 * PLATFORM CONSTANTS - SINGLE SOURCE OF TRUTH
 * ============================================
 * P0 FIX: All platform IDs MUST come from here.
 * 
 * CRITICAL: Use "brevo" for Email, NOT "email"
 * 
 * Setup Difficulty Scale (1-6):
 * 1 = Très simple (copier/coller)
 * 2 = Simple (Bot token)
 * 3 = Intermédiaire (API Key)
 * 4 = Avancé (OAuth App Dev)
 * 5 = Complexe (Page + permissions)
 * 6 = Très complexe (Pro + Page + Meta)
 */

// Setup modes for platforms
export const SETUP_MODES = {
  MANUAL: 'manual',      // Copy/paste - no API
  API_KEY: 'apiKey',     // Simple API key
  BOT_TOKEN: 'botToken', // Bot/channel token
  OAUTH: 'oauth'         // Full OAuth flow
};

// Difficulty levels with labels
export const DIFFICULTY_LEVELS = {
  1: { label: 'Très simple', color: '#22c55e', category: 'easy' },
  2: { label: 'Simple', color: '#84cc16', category: 'easy' },
  3: { label: 'Intermédiaire', color: '#eab308', category: 'medium' },
  4: { label: 'Avancé', color: '#f97316', category: 'medium' },
  5: { label: 'Complexe', color: '#ef4444', category: 'advanced' },
  6: { label: 'Très complexe', color: '#dc2626', category: 'advanced' }
};

// Canonical platform IDs - sorted by difficulty (easiest first)
// P0 FIX: Use x_twitter to match backend channels_config.x_twitter
export const PLATFORM_IDS = [
  'whatsapp',    // 1 - Mode manuel
  'telegram',    // 2 - Bot + Channel
  'brevo',       // 3 - API Key
  'pinterest',   // 4 - OAuth App Dev
  'x_twitter',   // 4 - OAuth App Dev (key matches backend channels_config.x_twitter)
  'facebook',    // 5 - Page + permissions
  'instagram',   // 6 - Pro + Page + Meta
  'linkedin',    // 5 - Pro account (non affiché par défaut)
  'blog'         // 3 - API config (non affiché par défaut)
];

// Platform display configuration with difficulty metadata
export const PLATFORMS_CONFIG = {
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp',
    color: '#25D366',
    gradient: 'linear-gradient(135deg, #25D366, #128C7E)',
    description: 'Copiez/collez le message pour publier manuellement',
    // Difficulty metadata
    setupDifficulty: 1,
    setupMode: SETUP_MODES.MANUAL,
    difficultyLabel: 'Très simple',
    difficultyHint: 'Mode manuel : copier/coller, pas de connexion OAuth.',
    shortHint: 'Mode manuel — Copier/coller',
    recommendedFor: 'Démarrage rapide, sans compte développeur'
  },
  telegram: {
    id: 'telegram',
    name: 'Telegram',
    color: '#0088cc',
    gradient: 'linear-gradient(135deg, #0088cc, #229ED9)',
    description: 'Messages automatiques vers un canal Telegram',
    // Difficulty metadata
    setupDifficulty: 2,
    setupMode: SETUP_MODES.BOT_TOKEN,
    difficultyLabel: 'Simple',
    difficultyHint: 'Créez un bot via @BotFather et copiez le token.',
    shortHint: 'Bot + Channel — tokens',
    recommendedFor: 'Publication auto sans config complexe'
  },
  brevo: {
    id: 'brevo',
    name: 'Email (Brevo)',
    color: '#0B996E',
    gradient: 'linear-gradient(135deg, #0B996E, #0B996E)',
    description: 'Campagnes email marketing automatiques',
    // Difficulty metadata
    setupDifficulty: 3,
    setupMode: SETUP_MODES.API_KEY,
    difficultyLabel: 'Intermédiaire',
    difficultyHint: 'Clé API Brevo + adresse expéditeur vérifiée.',
    shortHint: 'Clé API + expéditeur',
    recommendedFor: 'Newsletters et campagnes email'
  },
  pinterest: {
    id: 'pinterest',
    name: 'Pinterest',
    color: '#E60023',
    gradient: 'linear-gradient(135deg, #E60023, #BD081C)',
    description: 'Épingles automatiques sur vos tableaux',
    // Difficulty metadata
    setupDifficulty: 4,
    setupMode: SETUP_MODES.OAUTH,
    difficultyLabel: 'Avancé',
    difficultyHint: 'Créer une app sur Pinterest Developer Portal + OAuth.',
    shortHint: 'App Dev + OAuth + Board',
    recommendedFor: 'Contenu visuel et inspiration'
  },
  // P0 FIX: Key is x_twitter to match backend channels_config.x_twitter
  x_twitter: {
    id: 'x_twitter',
    name: 'X (Twitter)',
    color: '#000000',
    gradient: 'linear-gradient(135deg, #000000, #14171A)',
    description: 'Tweets automatiques',
    // Difficulty metadata
    setupDifficulty: 4,
    setupMode: SETUP_MODES.OAUTH,
    difficultyLabel: 'Avancé',
    difficultyHint: 'Créer une app sur X Developer Portal + OAuth 2.0.',
    shortHint: 'App Dev + OAuth',
    recommendedFor: 'Automatisation complète Twitter'
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    gradient: 'linear-gradient(135deg, #1877F2, #1877F2)',
    description: 'Publication sur votre page professionnelle',
    // Difficulty metadata
    setupDifficulty: 5,
    setupMode: SETUP_MODES.OAUTH,
    difficultyLabel: 'Complexe',
    difficultyHint: 'Page Facebook + permissions administrateur + app Meta.',
    shortHint: 'Page + permissions',
    recommendedFor: 'Pages professionnelles établies'
  },
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    color: '#E1306C',
    gradient: 'linear-gradient(135deg, #833AB4, #FD1D1D, #FCB045)',
    description: 'Publication automatique sur votre compte professionnel',
    // Difficulty metadata
    setupDifficulty: 6,
    setupMode: SETUP_MODES.OAUTH,
    difficultyLabel: 'Très complexe',
    difficultyHint: 'Compte pro + Page Facebook + permissions + liaisons Meta.',
    shortHint: 'Compte pro + Page + permissions + liaisons Meta',
    recommendedFor: 'Comptes Instagram Business établis'
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0A66C2',
    gradient: 'linear-gradient(135deg, #0A66C2, #0A66C2)',
    description: 'Publications professionnelles',
    // Difficulty metadata
    setupDifficulty: 5,
    setupMode: SETUP_MODES.OAUTH,
    difficultyLabel: 'Complexe',
    difficultyHint: 'App LinkedIn Developer + OAuth + permissions page.',
    shortHint: 'App Dev + OAuth',
    recommendedFor: 'Contenu B2B professionnel'
  },
  blog: {
    id: 'blog',
    name: 'Blog / Site web',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981, #14B8A6)',
    description: 'Articles de blog automatiques',
    // Difficulty metadata
    setupDifficulty: 3,
    setupMode: SETUP_MODES.API_KEY,
    difficultyLabel: 'Intermédiaire',
    difficultyHint: 'Configuration API selon votre CMS.',
    shortHint: 'API CMS',
    recommendedFor: 'SEO et content marketing'
  }
};

// Helper to get platforms sorted by difficulty
export const getPlatformsSortedByDifficulty = () => {
  return Object.values(PLATFORMS_CONFIG)
    .sort((a, b) => a.setupDifficulty - b.setupDifficulty);
};

// Helper to get platforms by category
export const getPlatformsByCategory = () => {
  const sorted = getPlatformsSortedByDifficulty();
  return {
    easy: sorted.filter(p => p.setupDifficulty <= 2),
    medium: sorted.filter(p => p.setupDifficulty >= 3 && p.setupDifficulty <= 4),
    advanced: sorted.filter(p => p.setupDifficulty >= 5)
  };
};

// Helper to check if platform is manual-only
export const isManualPlatform = (platformId) => {
  return PLATFORMS_CONFIG[platformId]?.setupMode === SETUP_MODES.MANUAL;
};

// Helper to get platform name
export const getPlatformName = (platformId) => {
  return PLATFORMS_CONFIG[platformId]?.name || platformId;
};

// Helper to get platform color
export const getPlatformColor = (platformId) => {
  return PLATFORMS_CONFIG[platformId]?.color || '#6B7280';
};

// Helper to get platform difficulty
export const getPlatformDifficulty = (platformId) => {
  return PLATFORMS_CONFIG[platformId]?.setupDifficulty || 3;
};

// Helper to get difficulty color
export const getDifficultyColor = (difficulty) => {
  return DIFFICULTY_LEVELS[difficulty]?.color || '#6B7280';
};

// Helper to get difficulty label
export const getDifficultyLabel = (difficulty) => {
  return DIFFICULTY_LEVELS[difficulty]?.label || 'Inconnu';
};

// Validate platform ID
export const isValidPlatform = (platformId) => {
  return PLATFORM_IDS.includes(platformId);
};

// P0 REFACTOR: Map frontend platform ID to backend API platform ID
// Frontend uses "x_twitter" for UI consistency, but backend API uses "x"
export const getApiPlatformId = (platformId) => {
  const mapping = {
    'x_twitter': 'x'  // Frontend x_twitter -> Backend x
  };
  return mapping[platformId] || platformId;
};

// P0 REFACTOR: Map backend platform ID to frontend platform ID
export const getFrontendPlatformId = (platformId) => {
  const mapping = {
    'x': 'x_twitter'  // Backend x -> Frontend x_twitter
  };
  return mapping[platformId] || platformId;
};

// Get all platforms as array for iteration (sorted by difficulty)
export const getPlatformsArray = () => {
  return getPlatformsSortedByDifficulty();
};

// Get main platforms for ConnectStep (excludes blog and linkedin by default)
// P0 FIX: Use x_twitter to match backend channels_config
export const getMainPlatforms = () => {
  const mainIds = ['whatsapp', 'telegram', 'brevo', 'pinterest', 'x_twitter', 'facebook', 'instagram'];
  return mainIds.map(id => ({
    id,
    ...PLATFORMS_CONFIG[id]
  }));
};
